export interface PaginatedResponse<T> {
  count: number;
  results: T[];
  next: string | null;
  previous: string | null;
}

// Strategy firm_type for Domestic AIF (matches backend Strategy.AIF). Offshore AIF
// variants (IFSC_AIF, SLHF_AIF) have other values and are intentionally excluded.
// Single source of truth — imported by the client-performance and client-form views.
export const AIF_FIRM_TYPE = 2;
export const IFSC_AIF_FIRM_TYPE = 3;

// report_type value the backend sets on IFSC client-performance payloads.
export const IFSC_REPORT_TYPE = 'IFSC_AIF';
// report_type value the backend sets on IBKR client-performance payloads.
export const IBKR_REPORT_TYPE = 'IBKR';

// Benchmark display labels shown alongside client-performance returns.
export const SP500_BENCHMARK_LABEL = 'S&P 500';
export const BSE500_BENCHMARK_LABEL = 'BSE500';

export type ClientRiskStatus = 'HIGH' | 'MEDIUM' | 'LOW';

export const CLIENT_RISK_STATUS_OPTIONS: { value: ClientRiskStatus; label: string }[] = [
  { value: 'HIGH', label: 'High Risk' },
  { value: 'MEDIUM', label: 'Medium Risk' },
  { value: 'LOW', label: 'Low Risk' },
];

export const CLIENT_RISK_STATUS_LABELS = Object.fromEntries(
  CLIENT_RISK_STATUS_OPTIONS.map((o) => [o.value, o.label])
) as Record<ClientRiskStatus, string>;

export interface Client {
  [key: string]: any;
  id: number;
  client_code: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  full_name?: string; // Added for list view compatibility
  email: string;
  contact_no?: string;
  pan_card?: string; // May not be present in list view
  fa_account_no: string;
  is_nri?: boolean; // May not be present in list view
  is_active: boolean;
  inception_date: string;
  activation_date?: string;
  agreement_date?: string;
  maturity_date?: string;
  performance_reporting_date?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
  client_type?: string;
  risk_status?: ClientRiskStatus | null;
  legacy_client?: boolean;

  // Fee Structure Fields
  management_fees?: string;
  hurdle_rate?: string;
  performance?: string;
  exit_load?: string;
  performance_calc_duration?: string;
  distributor_share?: string;
  distributor_rm_name?: string;
  relationship_manager_email?: string;
  fa_fees_to_be_charged?: boolean;
  is_tax_applicable?: boolean;

  // Banking Information
  bank_name?: string;
  bank_acc_no?: string;
  bank_micr?: string;
  bank_ifsc?: string;
  bank_branch_address?: string;

  // Trading Account
  savings_account_no?: string;
  pis_account_no?: string;

  // Trading/Investment Settings
  block_amount?: number;
  stop_purchase?: boolean;
  redemption_only?: boolean;
  clearing_cash_enable?: boolean;
  cash_threshold_limit?: number;
  is_derivative_consent?: boolean;
  restricted_companies?: number[];

  // Other Details
  occupation?: string;
  notes?: string;

  // Custody
  custody_code?: string;
  custody_dp?: string;
  custody_account_id?: string;
  mode_of_holding?: string;

  // Stakeholders
  nominee_name?: string;
  nominee_relationship?: string;
  nominee_2_name?: string;
  nominee_2_relationship?: string;
  guardian_name?: string;
  contact_person_name?: string;
  contact_person_email?: string;
  contact_person_contact_no?: string;
  ubo_name?: string;
  joint_holder_1_name?: string;
  joint_holder_1_pan?: string;
  joint_holder_2_name?: string;
  joint_holder_2_pan?: string;

  has_pending_approval?: boolean;
  created_ts?: string; // May not be present in list view
  updated_ts?: string; // May not be present in list view
  scheme?: {
    id: number;
    name: string;
  };
  scheme_name?: string; // Backend list API returns scheme_name directly
  firm_type?: number; // Backend list API returns the scheme's strategy firm_type
  category?: {
    id: number;
    name: string;
  };
  category_name?: string; // Backend list API returns category_name directly
  group?: {
    id: number;
    name: string;
  };
  distributor?: {
    id: number;
    name: string;
  };
}

export interface ClientFieldConfig {
  id: number;
  client_type: 'PMS' | 'AIF';
  field_name: string;
  is_required: boolean;
}

export interface ClientFormData {
  [key: string]: any;
  id?: number;
  client_code: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  email: string;
  contact_no: string;
  pan_card: string;
  fa_account_no: string;
  is_nri: boolean;
  inception_date: string;
  activation_date?: string;
  agreement_date?: string;
  maturity_date?: string;
  performance_reporting_date?: string;
  is_active?: boolean;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
  country_id?: number;
  legacy_client?: boolean;
  distributor_share: string;
  distributor_rm_name?: string;
  relationship_manager_email?: string;
  client_type: string;
  risk_status?: ClientRiskStatus | null;

  // Relations
  scheme: number;
  category?: number;
  group?: number;
  client_class?: number;
  distributor?: number;

  // Banking
  bank_name?: string;
  bank_acc_no?: string;
  bank_micr?: string;
  bank_ifsc?: string;
  bank_branch_address?: string;
  account_type?: string;

  // Trading Account
  savings_account_no?: string;
  pis_account_no?: string;

  // Trading
  block_amount?: number;
  stop_purchase?: boolean;
  redemption_only?: boolean;
  clearing_cash_enable?: boolean;
  cash_threshold_limit?: number;
  is_derivative_consent?: boolean;
  restricted_companies?: number[];

  // Other Details
  occupation?: string;
  notes?: string;

  // Custody
  custody_code?: string;
  custody_dp?: string;
  custody_account_id?: string;
  mode_of_holding?: string;

  // Stakeholders
  nominee_name?: string;
  nominee_relationship?: string;
  nominee_2_name?: string;
  nominee_2_relationship?: string;
  guardian_name?: string;
  contact_person_name?: string;
  contact_person_email?: string;
  contact_person_contact_no?: string;
  ubo_name?: string;
  joint_holder_1_name?: string;
  joint_holder_1_pan?: string;
  joint_holder_2_name?: string;
  joint_holder_2_pan?: string;
}


export interface FieldConfig {
  id: number;
  client_type: 'PMS' | 'AIF';
  field_name: string;
  is_required: boolean;
}

export interface FieldRequirements {
  client_type: 'PMS' | 'AIF';
  required_fields: string[];
}

export interface ClientListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Client[];
}

export interface Scheme {
  id: number;
  name: string;
  display_name?: string;
  code?: string;
  is_active: boolean;
  created_ts: string;
  updated_ts?: string;
  order_calc_type?: number;
  fa_scheme_id?: number;
  created_by?: number;
  updated_by?: number;
  region?: number;
  /**
   * "INR" or "USD". Server-derived from the scheme code, NOT from `region` — SGAF is
   * `region=GLOBAL` but trades in INR, so re-deriving this in the UI mislabels it.
   */
  base_currency?: string;
  investment_strategy?: string;
  investment_strategy_display?: string;
  acronym?: string;
  full_name?: string;
  strategy_firm_type?: number | null;
  /**
   * Daily fund-accounting bundle this scheme's holdings arrive in (`FASource.value`).
   * Null means the daily FA upload never refreshes this scheme's holdings.
   */
  fa_source?: string | null;
  fa_source_display?: string | null;
  /** Buy and sell orders on this scheme may be generated for selected clients only. */
  allow_client_wise_orders?: boolean;
}

/** Numeric value for the "Mutual Fund" order calculation type on a Scheme. */
export const ORDER_CALC_TYPE_MUTUAL_FUND = 2;

/** Human-readable labels for contract-file type values. */
export const FILE_TYPE_LABELS: Record<number, string> = {
  1: 'Equity',
  2: 'Mutual Fund',
  3: 'Derivative',
};

export interface SchemeFormData {
  name: string;
  code?: string;
  display_name?: string;
  is_active: boolean;
  fa_scheme_id?: number;
  investment_strategy?: number;
  acronym?: string;
  full_name?: string;
  fa_source?: string | null;
  allow_client_wise_orders?: boolean;
}

export interface SchemeListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Scheme[];
}

export interface ClientCategory {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
}

export interface ClientGroup {
  id: number;
  name: string;
  group_name: string; // Handle both cases just in case
  group_code?: string | null;
  description?: string;
  is_active: boolean;
}

export interface ClientClass {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
}

export interface Distributor {
  id: number;
  name: string;
  contact_info?: string;
  is_active: boolean;
}

export interface Strategy {
  id: number;
  name: string;
  code: string;
  firm_type?: number;
  firm_type_display?: string;
  is_active: boolean;
  created_ts: string;
  updated_ts?: string;
  deleted_ts?: string;
  created_by?: number;
  created_by_name?: string;
  updated_by?: number;
  updated_by_name?: string;
}

export interface StrategyFormData {
  name: string;
  code: string;
  firm_type?: number;
}

export interface StrategyListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Strategy[];
}

export interface FirmType {
  value: number;
  label: string;
}

export interface FirmTypesResponse {
  firm_types: FirmType[];
}

/**
 * One daily fund-accounting bundle, as returned by `GET /api/fa-processor/sources/`.
 * `value` is the backend `Scheme.fa_source` key (e.g. "PMS", "SGAF") — the same key
 * the FA upload and the scheme master run on. Not a `Strategy.firm_type` number.
 */
export interface FASource {
  value: string;
  label: string;
  accepts_xlsx?: boolean;
  cash_file_required?: boolean;
  is_api_based?: boolean;
  provisions_supported?: boolean;
}

export interface CompanyCategory {
  id: number;
  category: string;
  category_description?: string;
  is_invested_category: boolean;
  is_active: boolean;
}

export interface Country {
  id: number;
  name: string;
  code: string;
  is_active?: boolean;
}

export interface AssetClass {
  id: number;
  name: string;
  is_active: boolean;
}

export interface Sector {
  id: number;
  sector_name: string;
  sector_description?: string;
  is_active: boolean;
}

export interface Company {
  id: number;
  name: string;
  symbol: string;
  mf_scheme_code?: string;
  isin: string;
  bse_code?: string;
  nse_code?: string;
  accord_code?: number;
  alpha_sense_code?: string;
  cio_note?: string | null;
  country?: Country;
  restriction_for_nri: boolean;
  exclude_from_aum_calculation: boolean;
  asset_class?: AssetClass;
  asset_class_name?: string;
  category?: CompanyCategory;
  category_name?: string;
  sector?: Sector;
  sector_name?: string;
  is_active: boolean;
  created_ts: string;
  updated_ts?: string;
  deleted_ts?: string;
  created_by?: number;
  created_by_name?: string;
  updated_by?: number;
  updated_by_name?: string;
}

export interface CompanyFormData {
  name: string;
  symbol: string;
  mf_scheme_code?: string;
  isin: string;
  bse_code?: string;
  nse_code?: string;
  accord_code?: number | null;
  alpha_sense_code?: string;
  country?: number | null;
  restriction_for_nri?: boolean;
  exclude_from_aum_calculation?: boolean;
  is_active?: boolean;
  asset_class?: number | null;
  category?: number | null;
  sector?: number | null;
}

export interface CompanyListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Company[];
}

export interface Broker {
  id: number;
  broker_name: string;
  sebi_code: string;
  target_amt?: string;
  target_pct?: string;
  is_active: boolean;
  created_ts?: string;
  updated_ts?: string;
}

export interface SchemeWiseBroker {
  id: number;
  broker_code: string;
  is_default: boolean;
  broker: number;
  broker_name: string;
  broker_sebi_code: string;
  scheme: number;
  scheme_name: string;
  scheme_code: string;
  nri_client_master?: number;
  custody_code?: string;
  is_active: boolean;
  created_ts?: string;
  updated_ts?: string;
  deleted_ts?: string;
  created_by?: number;
  updated_by?: number;
}

export interface Currency {
  id: number;
  name: string;
  code: string;
  current_rate_INR?: number;
  updated_on?: string;
  is_active: boolean;
  created_ts?: string;
  updated_ts?: string;
}

export interface Holdings {
  id: number;
  holding_date: string;
  client_master: number | null;
  client_code: string;
  client_first_name?: string | null;
  client_middle_name?: string | null;
  client_last_name?: string | null;
  company: number;
  company_symbol: string;
  company_name: string;
  quantity: number;
  unit_cost?: number;
  unit_price: number;
  market_value: number;
  market_value_base: number;
  cost_value?: number;
  cost_value_base?: number;
  accrued_income?: number;
  percentage_of_portfolio?: number;
  adj_quantity: number;
  adj_unit_price: number;
  adj_unit_cost: number;
  currency: number;
  currency_code: string;
  base_currency: number;
  base_currency_code: string;
  exchange_rate: number;
  is_active: boolean;
  created_ts?: string;
  updated_ts?: string;
}

export interface HoldingsListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Holdings[];
}

export interface BankStatement {
  id: number;
  account_number: string;
  client_master: number;
  client_full_name: string | null;
  client_name: string;
  currency: string;
  net_balance: number;
  available_balance: number;
  statement_date: string;
  created_ts: string;
}

export interface BankStatementListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: BankStatement[];
}

export interface OrderAllocation {
  id: number;
  trade_date: string;
  client_master: number | null;
  client_code: string;
  client_first_name?: string | null;
  client_middle_name?: string | null;
  client_last_name?: string | null;
  symbol: number;
  symbol_code: string;
  company_name: string;
  broker_code: number;
  broker_code_display: string;
  contract_no: number;
  contract_no_display: string;
  sell_buy: number;
  sell_buy_display: string;
  quantity: number;
  mkt_rate: number;
  brokerage: number;
  stt: number;
  ser_tax: number;
  sebi_tax: number;
  turn_tax: number;
  stamp_duty: number;
  exit_load: number | null;
  net_rate: number;
  net_amt: number;
  adj_quantity: number;
  adj_mkt_rate: number;
  adj_net_rate: number;
  adj_net_amt: number;
  is_active: boolean;
  created_ts?: string;
}

export interface OrderAllocationListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: OrderAllocation[];
}

export interface HoldingsSummary {
  total_quantity?: number;
  total_market_value_base?: number;
  total_cost_value_base?: number;
  holdings_count?: number;
  unique_clients?: number;
  base_currency_code?: string;
}

export interface SchemeWiseHoldingsSummary {
  total_aum: number;
  scheme_aum: Record<string, number>;
  base_currency: string;
  holding_date: string;
}

// Backend-pivoted types for SchemeWiseHoldings (OMS-327)
export interface SchemeSlot {
  market_value: number;
  percentage_of_aum: number;
  unique_clients: number;
  total_quantity: number;
}

export interface PivotedSchemeRow {
  company_id: number;
  symbol: string;
  name: string;
  asset_class: string;
  schemes: Record<string, SchemeSlot>;
}

export interface SchemeWiseHoldingsResponse {
  results: PivotedSchemeRow[];
  scheme_totals: Record<string, { market_value: number; percentage_of_aum: number }>;
  summary: SchemeWiseHoldingsSummary;
  access_restricted?: boolean;
}

export interface HoldingsByClientResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Holdings[];
  summary: HoldingsSummary;
}

export interface HoldingsByCompanyResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Holdings[];
  summary: HoldingsSummary;
}


export interface ContractNote {
  id: number;
  contract_no: string;
  series?: string;
  sett_no?: number;
  sett_type?: string;
  sell_buy: number; // 1 for Buy, 2 for Sell
  trade_type: string; // "Buy" or "Sell"
  trade_date: string;
  party_code?: number;
  broker_code: string;
  broker_name: string;
  custody_code?: string;
  scheme_code: string;
  company?: number;
  company_symbol: string;
  company_name: string;
  quantity: number;
  mkt_rate: number;
  mkt_amount: number;
  brokerage?: number;
  stt?: number;
  ser_tax?: number;
  sebi_tax?: number;
  turn_tax?: number;
  stamp_duty?: number;
  total_charges: number;
  net_rate?: number;
  net_amt: number;
  adj_quantity: number;
  adj_net_rate: number;
  adj_mkt_rate: number;
  adj_net_amt: number;
  adj_mkt_amount: number;
  file_id?: number;
  party_code_name?: string;
  company_isin?: string;
  sell_buy_display?: string;
  is_allocated?: boolean;
  is_active: boolean;
  created_ts?: string;
  updated_ts?: string;
}

export interface ContractFile {
  id: number;
  file: string;
  type: number;
  contract_note_date: string | null;
  allocation_status?: 'full' | 'partial' | 'none';
  created_ts: string;
  updated_ts: string;
}
export type ContractFileResponse = ContractFile[];
export interface ContractFileUploadResponse {
  total_files: number;
  successful_uploads: number;
  failed_uploads: number;
  results: Array<{
    file: string;
    status: boolean;
    message: string;
    errors?: string[];
  }>;
}

export interface CustodianAllocationStatus {
  id: number;
  name: string;
  status: 'done' | 'pending';
  allocation_count: number;
  order_calc_type: number;
  order_calc_type_display: string;
}

/** Outcome of rebuilding a global (IBKR) scheme's contract notes from the live session. */
export interface GlobalContractNoteFetchResult {
  scheme: string;
  custody_id: number;
  order_calc_type: number;
  trade_date: string;
  contract_notes: number;
  contract_notes_replaced: number;
  errors: string[];
}

export interface ContractNoteListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ContractNote[];
}

export interface ContractNoteSummary {
  total_quantity?: number;
  total_market_amount?: number;
  total_net_amount?: number;
  total_charges?: number;
  trade_count?: number;
  unique_clients?: number;
  unique_companies?: number;
}

export interface ContractNotesByClientResponse {
  contract_notes: ContractNote[];
  summary: ContractNoteSummary;
}

export interface ContractNotesByCompanyResponse {
  contract_notes: ContractNote[];
  summary: ContractNoteSummary;
}

export interface CustodyHoldings {
  id: number;
  holding_date: string;
  client_master: number | null;
  client_code: string;
  client_name: string;
  company: number;
  company_symbol: string;
  company_name: string;
  saleable_qty: number;
  logical_qty: number;
  total_quantity: number;
  adj_saleable_qty: number;
  adj_logical_qty: number;
  is_fully_saleable: boolean;
  is_active: boolean;
  created_ts?: string;
  updated_ts?: string;
}

export interface CustodyHoldingsListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CustodyHoldings[];
}

/** Response type for the export-all endpoint, which returns all matching records in one shot.
 *  `count`/`next`/`previous` are omitted because pagination does not apply here. */
export interface CustodyHoldingsExportResponse {
  results: CustodyHoldings[];
}

export interface CustodyHoldingsSummary {
  total_saleable_qty?: number;
  total_logical_qty?: number;
  total_quantity?: number;
  holdings_count?: number;
  unique_clients?: number;
  unique_companies?: number;
  fully_saleable_count?: number;
  partially_saleable_count?: number;
}

export interface CustodyHoldingsByClientResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CustodyHoldings[];
  summary: CustodyHoldingsSummary;
}

export interface CustodyHoldingsByCompanyResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CustodyHoldings[];
  summary: CustodyHoldingsSummary;
}

export interface TradeSuggestion {
  trade_type: string | null;
  trade_description: string | null;
}

export interface Recommendation {
  recommended_by: string;
  recommendation_type: string;
  decision: string;
  decision_price: number;
  target_weight: number | null;
  target_price: number | null;
  assumption_years: number | null;
  rec_date: string;
  target_date: string | null;
  created_ts: string | null;
  estimate_type: string | null;
  comment: string;
  suggestions: {
    [strategy_code: string]: TradeSuggestion;
  };
}

export interface StrategyData {
  aum: number;
  client_count: number;
  min_client_holding: number;
  median_client_holding: number;
  max_client_holding: number;
  // Count of accounts holding below the CIO target (or any cash-holder when no target is set)
  // that still have deployable cash. Drives the "accounts that can still buy" tint.
  // NOTE: distinct from the per-firm-type field of the same name in
  // components/order_recommendation/types.ts (holdings_stats_by_firm_type) — that is
  // optional/nullable and scoped to an order row; this one is per-strategy and always present.
  accounts_below_target_with_cash: number;
}

/**
 * `ltp` is the quoted price in its currency group's canonical unit — pence are published
 * as pounds — and `currency_code` names that currency (null when market data reports
 * none). A price not in the scheme's `base_currency` cannot size an order.
 */
export interface LivePrice {
  company_id: number;
  ltp: number | null;
  currency_code: string | null;
  exchange_code: string | null;
  ltp_timestamp: string | null;
}

export interface TradeSuggestionsItem {
  nse_code: string | null;
  bse_code: string | null;
  isin_number: string;
  company_name: string;
  symbol: string;
  is_invested: boolean;
  category: string | null;
  country_code: string | null;
  // Currency of the recommendation prices (from raw), e.g. "USD". Shown on Global rows.
  currency_code: string | null;
  ltp: number | null;
  ltp_timestamp: string | null;
  // Currency of the LTP/CMP (from live market data), e.g. "USD". Shown on Global rows.
  ltp_currency_code: string | null;
  // Who the company is assigned to now, and who gave the latest recommendation in each
  // role. The two differ once a company changes hands, and the UI tags the difference.
  analyst_name: string | null;
  ic_user_name: string | null;
  latest_rec_analyst_name: string | null;
  latest_rec_ic_user_name: string | null;
  strategies: {
    [strategy_code: string]: StrategyData;
  };
  recommendations: Recommendation[];
}

export interface TradeSuggestionsResponse {
  message: string;
  data: TradeSuggestionsItem[];
  meta: {
    total_companies: number;
    total_strategies: number;
    filters_applied: {
      categories_list: string;
      isin_list: string;
      countries_list: string;
      strategy_codes: string[];
      start_date: string;
      end_date: string;
      suggestions_flag: boolean;
    };
  };
}


export interface Notifications {
  id: number;
  notification_id: number;
  notification_type: string;
  title: string;
  message: string;
  is_read: boolean;
  is_completed: boolean;
  created_ts: string;
  module: string;
  is_actionable: boolean;
  action_url: string;
  last_reminded_at?: string;
}

/** A client form picker option; `value` is "Source:series_key". */
export interface ClientSeriesOption {
  value: string;
  label: string;
  selected: boolean;
  /** Other clients already holding this series; a series can be shared. */
  held_by: number;
}

export interface XirrPerformanceData {
  status: string;
  msg: string;
  xirr: number | null;
  xirr_percent: number | null;
  annualized_percent: number | null;
  start_date: string | null;
  end_date: string | null;
  last_working_date: string;
  days: number | null;
  starting_aum: number | null;
  final_aum: number | null;
}

export interface TwrrPerformanceData {
  status: string;
  msg: string;
  twrr: number | null;
  twrr_percent: number | null;
  annualized_percent: number | null;
  start_date: string | null;
  end_date: string | null;
  last_working_date: string;
  days: number | null;
  starting_aum: number | null;
  final_aum: number | null;
}

export interface BenchmarkPerformanceData {
  status: string;
  msg: string;
  start_date_price: number;
  end_date_price: number;
  start_date: string;
  end_date: string;
  change_percent: number;
}

export interface BenchmarkData {
  name: string;
  symbol: string;
  calendar_ytd: BenchmarkPerformanceData;
  financial_ytd: BenchmarkPerformanceData;
  mtd: BenchmarkPerformanceData;
  this_week: BenchmarkPerformanceData;
  '1d': BenchmarkPerformanceData;
  custom_range?: BenchmarkPerformanceData;
}

export interface XirrSchemePerformance {
  scheme_name: string;
  scheme_code: string;
  display_name: string;
  acronym?: string;
  full_name?: string;
  calendar_ytd: XirrPerformanceData;
  financial_ytd: XirrPerformanceData;
  mtd: XirrPerformanceData;
  this_week: XirrPerformanceData;
  '1d': XirrPerformanceData;
  custom_range?: XirrPerformanceData;
  has_delayed_nav?: boolean; // Indicates if the scheme has delayed NAV data
}

export interface TwrrSchemePerformance {
  scheme_name: string;
  scheme_code: string;
  display_name: string;
  acronym?: string;
  full_name?: string;
  calendar_ytd: TwrrPerformanceData;
  financial_ytd: TwrrPerformanceData;
  mtd: TwrrPerformanceData;
  this_week: TwrrPerformanceData;
  '1d': TwrrPerformanceData;
  custom_range?: TwrrPerformanceData;
  has_delayed_nav?: boolean; // Indicates if the scheme has delayed NAV data
}

export interface XirrPortfolioPerformanceData {
  [schemeId: string]: XirrSchemePerformance;
}

export interface XirrPortfolioPerformanceResponse {
  current?: XirrPortfolioPerformanceData;
  last_working?: XirrPortfolioPerformanceData;
  custom?: XirrPortfolioPerformanceData;
}

export interface TwrrPortfolioPerformanceData {
  [schemeId: string]: TwrrSchemePerformance;
}

export interface TwrrPortfolioPerformanceResponse {
  current?: TwrrPortfolioPerformanceData;
  last_working?: TwrrPortfolioPerformanceData;
  custom?: TwrrPortfolioPerformanceData;
}

export interface BenchmarkPerformanceDataMap {
  [benchmarkId: string]: BenchmarkData;
}

export interface BenchmarkPerformanceResponse {
  current?: BenchmarkPerformanceDataMap;
  last_working?: BenchmarkPerformanceDataMap;
  custom?: BenchmarkPerformanceDataMap;
}

export interface CompanyVolumeStatsData {
  latest_median_volume: number;
  timeseries: {
    date: string;
    volume: number;
    median_volume_21d: number;
  }[];
}


export interface ExecutionAnalyticsRequest {
  from_date: string;
  to_date: string;
  company_id: number | null;
}

export interface DailyPOVData {
  date: string;
  buy_pov: number;
  sell_pov: number;
  total_pov: number;
  message?: string;
}

export interface VWAPData {
  date: string;
  vwap: number;
  buy_avg_price: number | null;
  sell_avg_price: number | null;
}

export interface BuyPriceVsVWAPData {
  date: string;
  buy_avg_price_vs_vwap: number;
}

export interface SellPriceVsVWAPData {
  date: string;
  sell_avg_price_vs_vwap: number;
}

export interface ExecutionAnalyticsResponse {
  company_name: string;
  daily_pov: DailyPOVData[];
  vwap_data: VWAPData[];
  buy_price_vs_vwap: BuyPriceVsVWAPData[];
  sell_price_vs_vwap: SellPriceVsVWAPData[];
}

export interface SelectOption {
  value: string;
  label: string;
  id?: number;
  metadata?: {
    investment_strategy?: string;
  };
}

export interface BrokerageComparisonRequest {
  from_date: string;
  to_date: string;
  trade_type?: string;
}

export interface BrokerComparisonData {
  broker_code: string;
  broker_name: string;
  total_trades: number;
  total_notional: number;
  avg_notional: number;
  vwap_slippage: number;
  brokerage: number;
  total_slippage: number;
}


export interface PortfolioLiquidityRiskRequest {
  scheme_codes?: string;
  /** Comma-separated company ids to include beyond held positions (e.g. not-yet-held buy recs). */
  company_ids?: string;
}

export interface PortfolioLiquidityRiskItem {
  company_id: number;
  company_symbol: string | null;
  current_position: number;
  notional: number;
  max_permissible_position: number;
  is_median_value_available: boolean;
  median_tv_21d: number;
}
// TypeScript
export interface PortfolioLiquidityRiskResponse {
  data: PortfolioLiquidityRiskItem[];
  meta: {
    total_companies: number;
    holding_date: string;
    aum: number;
  };
}

export interface ExposureRiskRequest {
  scheme_codes?: string;
}

export interface ExposureRiskItem {
  days: number;
  current_exposure_percent: number;
  minimum_exposure_percent: number;
}

export interface ExposureRiskResponse {
  data: ExposureRiskItem[];
  meta: {
    aum: number;
    total_companies: number;
    companies_with_no_median_value: Array<string>;
  };
}

// Base versioned model interface
export interface VersionedModel {
  valid_from: string;
  valid_to: string | null;
  is_active: boolean;
  supersedes: number | null;
}

// Performance Fee Structure
export interface PerformanceFeeStructure {
  id: number;
  fee_component: number;
  step_no: number;
  hurdle_type: string;
  hurdle_type_display?: string;
  hurdle_type_choices?: Array<{ value: string; label: string }>;
  hurdle_benchmark?: string;
  hurdle_benchmark_display?: string;
  hurdle_benchmark_choices?: Array<{ value: string; label: string }>;
  hurdle_value?: number | string;
  fee_value: number | string;
}

// Fee Component (Versioned)
export interface FeeComponentData extends VersionedModel {
  id?: number;
  client_code: string;
  scheme: number;
  performance_fee_calc_duration?: '3yearly' | 'quarterly' | 'yearly';
  management_fee_calc_duration?: '3yearly' | 'quarterly' | 'yearly';
  exit_load?: string;
  distributor_manage_fee_share: number;
  distributor_performance_fee_share: number;
}

// Fee Component with nested structures
export interface FeeComponent extends FeeComponentData {
  fee_structures: PerformanceFeeStructure[];
}

// Contribution (Versioned)
export interface ContributionData extends VersionedModel {
  id?: number;
  client_code: string;
  scheme: number;
  amount: number;
  top_up_amount: number;
  currency_code?: string;
}

// Supporting data types
export interface Scheme {
  id: number;
  name: string;
  is_active: boolean;
}

export interface ClientCategory {
  id: number;
  name: string;
  is_active: boolean;
}

export interface ClientClass {
  id: number;
  name: string;
  is_active: boolean;
}

export interface Distributor {
  id: number;
  name: string;
  is_active: boolean;
}

// Maker-Checker Approval Types
export interface ApprovalDataSnapshot {
  clientmaster?: Client;
  client?: Client;
  clientfeecomponent: FeeComponentModel[];
  performancefeestructure: PerformanceFeeStructure[];
  contribution: ContributionData[];
  /** The client's series before the change; missing on snapshots taken before series were tracked. */
  clientseriesmapping?: { id: number; series_code: string; fa_account_no: string; scheme_code: string }[];
}

// Client Approval for list view
export interface ClientApprovalListItem {
  id: number;
  object_details: Client;
  approval_status: 'Pending' | 'Approved' | 'Rejected';
  created_by: string;
  created_by_name: string;
  created_ts: string;
  created_at?: string;
  change_summary?: string; // e.g., "Client + 2 Fees + 1 Contribution"
  action_type?: 'CREATE' | 'UPDATE' | 'ACTIVATE' | 'DEACTIVATE';
  change_reason?: string;
}

export interface ClientApprovalListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ClientApprovalListItem[];
}

// Full approval detail with client data
export interface ClientApproval {
  id: number;
  object_type: number;
  object_id: number;
  client_type: string; // 'client' or 'aifclient'
  approval_status: 'Pending' | 'Approved' | 'Rejected';
  action_type?: 'CREATE' | 'UPDATE' | 'ACTIVATE' | 'DEACTIVATE';
  created_by: {
    id: number;
    username: string;
    email: string;
  };
  created_ts: string;
  checked_by?: {
    id: number;
    username: string;
    email: string;
  };
  checked_ts?: string;
  checker_comments?: string;
  rejection_reason?: string;
  change_reason?: string;
  data: ApprovalDataSnapshot;
  // For convenience, also include parsed client data
  object_details: Client;
}

// Approval filters
export interface ApprovalFilters {
  approval_status?: 'Pending' | 'Approved' | 'Rejected';
  created_by?: number;
  client_code?: string;
  page?: number;
  page_size?: number;
}

// Approval action payloads
export interface ApprovePayload {
  checker_comments?: string;
}

export interface RejectPayload {
  rejection_reason: string;
  checker_comments?: string;
}

export interface ClientFeeNote {
  id: number;
  note: string;
  created_ts: string;
}

// For FeeStructure component compatibility
export interface FeeComponentModel {
  id?: number;
  client_code: string;
  scheme: number;
  management_fee?: number;
  supersedes?: number;
  performance_fee_calc_duration?: string;
  performance_fee_calc_duration_display?: string;
  management_fee_calc_duration?: string;
  management_fee_calc_duration_display?: string;
  duration_choices?: Array<{ value: string; label: string }>;
  exit_load?: string;
  distributor_manage_fee_share: number;
  distributor_performance_fee_share: number;
  fee_structures: PerformanceFeeStructure[];
  valid_from?: string;
  valid_to?: string | null;
  is_active?: boolean;
}

// Contribution form data
export interface ContributionFormData {
  id?: number;
  client_code: string;
  scheme: number;
  valid_from: string;
  valid_to?: string | null;
  amount: number;
  top_up_amount: number;
  redemption_amount?: number;
  currency_code?: string;
  is_active?: boolean;
}

export interface ExposureRiskRequest {
  scheme_codes?: string;
}

export interface ExposureRiskItem {
  days: number;
  current_exposure_percent: number;
  minimum_exposure_percent: number;
}

export interface ExposureRiskResponse {
  data: ExposureRiskItem[];
  meta: {
    aum: number;
    total_companies: number;
    companies_with_no_median_value: Array<string>;
  };
}

/** One custody account a statement can be uploaded (or fetched) for. */
export interface CustodyAccount {
  id: number;
  custody_code: string;
  scheme: number;
  scheme_name?: string;
  /** Account issues no statement — its holdings are pulled from the broker API. */
  is_api_based: boolean;
}

export interface CustodyHoldingFile {
  id: number;
  custody_holding_date: string;
  /** The account, read off the custodian's filename — files carry no account column. */
  custody_code: string;
  file: string;
  file_name?: string;
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  error_detail?: string;
  holdings_count?: number;
  created_ts: string;
  updated_ts: string;
}

/** A row the statement reported that could not be matched to a client or company. */
export interface CustodySkippedRow {
  client_code: string;
  isin: string;
  reason: string;
}

export interface CustodyIngestResponse {
  status: boolean;
  error: string;
  written: number;
  ignored: number;
  skipped: CustodySkippedRow[];
  file: CustodyHoldingFile | null;
}

export interface CustodyHoldingFileListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CustodyHoldingFile[];
}

export interface BenchmarkWeightDetail {
  benchmark_name: string;
  weight: number;
}

export interface SectorCompanyDetail {
  name: string;
  symbol: string;
  weight: number;
  aum: number;
}

export interface BenchmarkCompanyDetail {
  name: string;
  symbol: string;
  weight: number;
  /**
   * Free-float market capitalisation in crores. Optional and nullable because
   * some benchmark constituents may not have market cap data available from
   * the benchmark provider; these are surfaced via the missing_free_float_market_cap alert.
   */
  free_float_market_cap_cr?: number | null;
}

export interface SectorWeightDetail {
  sector_name: string;
  portfolio_weight: number;
  portfolio_aum: number;
  is_unclassified?: boolean;
  benchmarks: BenchmarkWeightDetail[];
  companies: SectorCompanyDetail[];
  benchmark_companies?: { [benchmark: string]: BenchmarkCompanyDetail[] };
}

export interface SectorWeightAlerts {
  missing_free_float_market_cap: {
    [benchmark: string]: { name: string; symbol: string }[];
  };
  unclassified_companies: { name: string; symbol: string }[];
}

export interface SectorWeightResponse {
  message: string;
  data: SectorWeightDetail[];
  meta: {
    holding_date: string;
    total_aum: number;
    benchmarks: string[];
    total_sectors: number;
    alerts?: SectorWeightAlerts;
    access_restricted?: boolean;
  };
}

export interface SectorWeightRequest {
  scheme_codes?: string;
  benchmark?: string;
  investment_strategy_codes?: string;
  /** ISO date string (YYYY-MM-DD). When omitted, the backend defaults to the last working date. */
  date?: string;
}

export interface BrokerWiseBrokerageRequest {
  from_date: string;
  to_date: string;
}

export interface BrokerWiseBrokerageData {
  id: number;
  broker_name: string;
  sebi_code: string;
  total_brokerage: number;
  by_pct: number;
  target_pct: number;
  target_amt: number;
}

// --- Order Execution ---

export interface OrderExecution {
  id: number;
  company: number;
  company_name: string;
  company_symbol: string;
  scheme: number;
  scheme_code: string;
  scheme_name: string;
  order_side: 1 | 2;
  order_side_display: string;
  buy_order: number | null;
  sell_order: number | null;
  source_order_id: number | null;
  execution_date: string;
  limit_price: number;
  ordered_qty: number;
  executed_qty: number;
  execution_status: 1 | 2 | 3 | 4 | 5;
  execution_status_display: string;
  total_pov_percentage: number;
  remarks: string;
  created_ts: string;
  updated_ts: string;
}

export interface SchemeExecutionCell {
  id: number;
  ordered_qty: number;
  executed_qty: number;
  execution_status: number;
  execution_status_display: string;
  limit_price: number;
  scheme_name: string;
  pov_percentage: number | null;
  is_pov_excluded: boolean;
  source_order?: ({
    id: number;
    order_date: string;
    order_type_display: string;
    method_display: string;
    min_order_val: number;
    median_volume_pct: number;
    apply_volume_cap: boolean;
  } & (
    | {
        // buy order fields
        limit_price: number;
        cl1: number; cl2: number;
        increase_to_1: number; increase_to_2: number;
        increment_max_1: number; increment_max_2: number;
        increase_by_1: number; increase_by_2: number;
        max_holding: number;
        h1?: never; h2?: never; condition_1?: never; condition_2?: never;
      }
    | {
        // sell order fields
        h1: number; h2: number;
        condition_1: number; condition_2: number;
        limit_price?: never; cl1?: never; cl2?: never;
        increase_to_1?: never; increase_to_2?: never;
        increment_max_1?: never; increment_max_2?: never;
        increase_by_1?: never; increase_by_2?: never;
        max_holding?: never;
      }
  )) | null;
}

export interface ExecutionMatrixRow {
  company: { id: number; symbol: string; name: string };
  limit_price: number;
  total_pov_percentage: number | null;
  pov_error: string | null;
  schemes: Record<string, SchemeExecutionCell | null>;
}

export interface ExecutionMatrixResponse {
  defaults: { total_pov: number; min_pov: number; max_pov: number };
  schemes: string[];
  results: ExecutionMatrixRow[];
}

export interface LimitPriceLogEntry {
  id: number;
  limit_price: number;
  cmp_at_change: number | null;
  changed_at: string;
  changed_by: number | null;
  changed_by_name: string | null;
}

export interface ClientOrderGenerationFailureInfo {
  id: number;
  order_type: number;
  order_type_display: string;
  order_id: number;
  company_name: string;
  reason: string;
  client_master?: number | null;
  client_code: string;
  client_name: string;
  created_at: string;
}

export type ClientOrderGenerationFailureInfoListResponse = PaginatedResponse<ClientOrderGenerationFailureInfo>;

// Reason filter options for the order generation failures page. `reason` is free text,
// so the backend catalogues it into categories and filters on `reason_category`.
export interface FailureReasonCategory {
  value: string;
  label: string;
}

// Holdings date restriction capability check
export interface HoldingsDateRestriction {
  restricted: boolean;
  min_allowed_date: string | null;
}

// System settings (superuser only)
export interface Setting {
  id: number;
  name: string;
  code: string;
  value: string;
  field_type: number;
  field_type_display: string;
  is_active: boolean;
  updated_ts: string;
}

// ---- Contribution Analysis ------------------------------------------------

export interface ContributionRow {
  company_id: number;
  symbol: string;
  name: string;
  sector_name: string;
  beginning_mv: number;
  beginning_asset_pct: number;
  purchases: number;
  sales: number;
  realized_gain: number;
  unrealized_gain: number;
  income: number;
  ending_mv: number;
  ending_asset_pct: number;
  total_gain_loss: number;
  contribution: number;
  /** What the portfolio earned on the holding: daily gain ÷ prior-day position MV, chained.
   *  Never null — the backend coerces non-finite values to 0, and holdings whose gains are
   *  forced to zero (cash, futures margin) have a genuine 0% return. */
  position_return: number;
  /** The stock's own move — close before the start date → close on the end date. Null (not 0)
   *  when no price history covers either boundary: cash, receivables and unlisted holdings
   *  have no market move to report, which is unknown rather than flat. */
  stock_price_return: number | null;
}

export interface ContributionDataError {
  holding_date: string;
  client_code: string;
  symbol: string;
  name: string;
  qty_diff: number;
  traded_qty: number;
  missing_qty: number;
}

export interface ContributionAnalysisResponse {
  results: ContributionRow[];
  data_errors: ContributionDataError[];
  count: number;
  portfolio_return?: number | null;
  // Set by the backend when the requested period ends beyond the user's date-access ceiling.
  access_restricted?: boolean;
}

export interface UsdInrRate {
  rate_date: string | null;
  usd_inr: number | null;
}

// awaiting_conversion: no Zoho Contact carries the FA account number yet — the lead
// is unconverted. Not a failure; the daily sync picks the client up once it is.
export type ZohoSyncOutcome = 'synced' | 'awaiting_conversion' | 'failed';

export interface ZohoClientSyncResult {
  outcome: ZohoSyncOutcome;
  client_code: string;
}

// synced + failed + awaiting_conversion === total.
export interface ZohoSyncTotals {
  total: number;   // Clients found stale, i.e. attempted.
  synced: number;
  failed: number;
  awaiting_conversion: number;
  failed_fa_accounts: string[];
  duplicated_fa_accounts: string[];  // Subset of failed: on more than one Zoho Contact.
  awaiting_conversion_fa_accounts: string[];
}
